import { Component, computed, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { ActivatedRoute, Router } from "@angular/router";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { RequestService } from "@services/index";

@Component({
  selector: "fh-category-list",
  templateUrl: "./category-list.component.html",
  styleUrls: ["./category-list.component.css"],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    DataTableComponent,
    RefreshButtonComponent,
  ],
})
export class CategoryListComponent implements OnInit {
  /** Bound wrapper for the refresh button — see `getCategories()`. */
  refreshData = (): void => this.getCategories();
  // State signals
  categories = signal<any[]>([]);
  total = signal<number>(0);
  isLoading = signal<boolean>(false);
  pageIndex = signal<number>(0);
  pageSize = signal<number>(25);
  sortProp = signal<string>("id");
  sortDir = signal<"asc" | "desc">("asc");
  searchTerm = signal<string>("");
  statusFilter = signal<string>("");

  success_msg = signal<string>("");

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.categories(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: [10, 25, 50, 100],
    initialPageSize: 25,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/category_input/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "delete",
      label: "Delete Category",
      handler: (row, event) => this.deleteCategory(row.id, event),
      color: "warn",
      show: (row) => row.parent_id !== 0, // Only show delete for sub-categories
    },
  ]);

  constructor(
    private router: Router,
    private auth: RequestService,
    _route: ActivatedRoute
  ) {
    const navigation = this.router.getCurrentNavigation()!;
    const state = navigation.extras.state as {
      success_msg: string;
      error_msg: string;
    };
    if (state !== undefined) {
      if (state.success_msg) {
        this.success_msg.set(state.success_msg);
        setTimeout(() => {
          this.success_msg.set("");
        }, 3000);
      }
    }
  }

  ngOnInit(): void {
    this.getCategories();
  }

  getCategories(): void {
    this.isLoading.set(true);

    // Build query params
    const params = new URLSearchParams();
    params.append("page", (this.pageIndex() + 1).toString());
    params.append("pageSize", this.pageSize().toString());

    if (this.searchTerm()) {
      params.append("search", this.searchTerm());
    }

    if (this.statusFilter()) {
      params.append("status", this.statusFilter());
    }

    this.auth.get(`/api/v2/categories?${params.toString()}`).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.categories.set(res.data?.categories || []);
          this.total.set(res.data?.total || 0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.categories.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  onSearch(): void {
    this.pageIndex.set(0); // Reset to first page
    this.getCategories();
  }

  onStatusFilterChange(): void {
    this.pageIndex.set(0); // Reset to first page
    this.getCategories();
  }

  addCategory(): void {
    this.router.navigate(["/category_input/0"]);
  }

  deleteCategory(id: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (!confirm("Are you sure you want to delete this category?")) {
      return;
    }

    this.auth.delete(`/api/v2/categories/${id}`).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          alert(res.message || "Category deleted successfully");
          this.getCategories(); // Refresh the list
        } else {
          alert(res.message || "Failed to delete category");
        }
      },
      error: (err) => {
        alert(`Error deleting category: ${err.error?.message || "Unknown error"}`);
      },
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.sortProp.set(event.prop);
    this.sortDir.set(event.dir as "asc" | "desc");
    // Implement sorting if needed
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.getCategories();
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      {
        key: "id",
        header: "ID",
        sortable: true,
        width: "80px",
      },
      {
        key: "category_name",
        header: "Category Name",
        sortable: true,
        width: "200px",
      },
      {
        key: "category_img",
        header: "Image",
        sortable: false,
        width: "100px",
        type: "image",
      },
      {
        key: "parent_name",
        header: "Parent Category",
        sortable: false,
        width: "180px",
        format: (value) => value || "—",
      },
      {
        key: "parent_id",
        header: "Category Type",
        sortable: false,
        width: "120px",
        format: (value) => (value === 0 ? "Main" : "Sub"),
      },
      {
        key: "is_featured",
        header: "Is Featured",
        sortable: false,
        width: "120px",
      },
      {
        key: "status",
        header: "Status",
        sortable: false,
        width: "100px",
        cssClass: (row) => `status-${row.status?.trim()}`,
        format: (value) => value?.trim() || "—",
      },
    ];
  }
}
